import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import {
  QrCode,
  CheckCircle,
  XCircle,
  Warning,
  Camera,
  ArrowCounterClockwise,
  SignOut,
  Crown,
  BriefcaseMetal,
  User as UserIcon,
  Keyboard,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CLASS_BADGE = {
  guest: { label: "Guest", icon: UserIcon, color: "text-white/80 border-white/40" },
  sponsor_team: { label: "Sponsor Team", icon: BriefcaseMetal, color: "text-cyber-green border-cyber-green" },
  sponsor_request: { label: "Sponsor Request", icon: Crown, color: "text-cyber-yellow border-cyber-yellow" },
};

const CheckinScreen = () => {
  const { admin, logout, bootLoading } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [stats, setStats] = useState({ approved: 0, checked_in: 0, outstanding: 0 });
  const [recent, setRecent] = useState([]);
  const [result, setResult] = useState(null); // last scan result
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  // Manual field expected: a raw QR string. Also used to pre-fill if needed.
  const [manualValue, setManualValue] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const scannerRef = useRef(null);
  const lastScanRef = useRef({ value: null, at: 0 });

  const loadStats = useCallback(async () => {
    const params = eventId ? { event_id: eventId } : {};
    const [{ data: s }, { data: r }] = await Promise.all([
      api.get("/staff/check-in/stats", { params }),
      api.get("/staff/check-in/recent", { params: { ...params, limit: 20 } }),
    ]);
    setStats(s);
    setRecent(r);
  }, [eventId]);

  useEffect(() => {
    api.get("/staff/events").then(({ data }) => setEvents(data));
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const processScan = useCallback(async (qrText) => {
    // Debounce repeat reads of the same code (html5-qrcode fires continuously)
    const now = Date.now();
    if (lastScanRef.current.value === qrText && now - lastScanRef.current.at < 2500) return;
    lastScanRef.current = { value: qrText, at: now };

    setBusy(true);
    try {
      const { data } = await api.post("/staff/check-in", {
        qr_payload: qrText,
        event_id: eventId || undefined,
      });
      setResult({ ...data, at: Date.now() });
      // haptic + audio feedback
      if (navigator.vibrate) navigator.vibrate(data.ok ? 80 : [80, 60, 80]);
      loadStats();
    } catch (e) {
      setResult({ ok: false, code: "error", message: e?.response?.data?.detail || "Scan failed", at: Date.now() });
    } finally {
      setBusy(false);
    }
  }, [eventId, loadStats]);

  const startScanner = useCallback(async () => {
    setCameraError("");
    try {
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode("qr-reader-region");
      }
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (decodedText) => processScan(decodedText),
        () => { /* ignore per-frame decode misses */ },
      );
      setScanning(true);
    } catch (e) {
      setCameraError(e?.message || "Could not access camera. Check browser permissions.");
      setScanning(false);
    }
  }, [processScan]);

  const stopScanner = useCallback(async () => {
    try {
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }
    } catch (e) { console.debug("qr scanner stop failed (safe to ignore):", e); }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => { stopScanner(); };
  }, [stopScanner]);

  const handleManual = async (e) => {
    e.preventDefault();
    if (!manualValue.trim()) return;
    await processScan(manualValue.trim());
    setManualValue("");
  };

  // Check in a person picked from the search list (bypasses QR scan)
  const checkInById = async (rsvpId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/check-in", {
        rsvp_id: rsvpId,
        event_id: eventId || undefined,
      });
      setResult({ ...data, at: Date.now() });
      if (navigator.vibrate) navigator.vibrate(data.ok ? 80 : [80, 60, 80]);
      setSearchQ("");
      setSearchResults([]);
      loadStats();
    } catch (e) {
      setResult({ ok: false, code: "error", message: e?.response?.data?.detail || "Check-in failed", at: Date.now() });
    } finally {
      setBusy(false);
    }
  };

  // Debounced live search
  useEffect(() => {
    if (!manualOpen) return;
    const q = searchQ.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearchBusy(true);
      try {
        const { data } = await api.get("/staff/search", {
          params: { q, event_id: eventId || undefined, limit: 10 },
        });
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchBusy(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [searchQ, eventId, manualOpen]);

  if (bootLoading) return <div className="p-12 font-mono-tech text-white/40 bg-cyber-black min-h-screen">/ booting...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const selectedEvent = events.find((e) => e.id === eventId);

  return (
    <div className="min-h-screen bg-cyber-black cyber" data-testid="checkin-page">
      {/* HEADER */}
      <header className="sticky top-0 z-20 bg-cyber-black/95 backdrop-blur border-b border-cyber-line">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <QrCode size={24} weight="duotone" className="text-cyber-green shrink-0" />
            <div className="min-w-0">
              <div className="font-mono-tech text-cyber-yellow text-[11px] tracking-[0.3em]">FINTECHOMNI</div>
              <div className="font-mono-tech text-[10px] text-white/60 uppercase tracking-[0.15em] truncate">/ door check-in · {admin.name || admin.email}</div>
            </div>
          </div>
          <button onClick={logout} className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red hover:text-white flex items-center gap-1.5" data-testid="checkin-logout">
            <SignOut size={12} weight="bold" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT: scanner + result */}
        <div className="lg:col-span-7 space-y-4">
          {/* EVENT PICKER */}
          <div className="hard-border bg-cyber-surface p-4">
            <div className="term-label mb-2">/ event</div>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="w-full px-3 py-3 bg-cyber-black border border-cyber-line text-white"
              data-testid="checkin-event-select"
            >
              <option value="">All events (auto-detect from QR)</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>{e.title}</option>
              ))}
            </select>
            {selectedEvent && (
              <p className="font-mono-tech text-[10px] text-white/50 mt-2">
                / guarding door for {selectedEvent.title}. Wrong-event QRs will be rejected.
              </p>
            )}
          </div>

          {/* SCANNER */}
          <div className="hard-border bg-cyber-surface p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="term-label">/ scanner</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setManualOpen((v) => !v)}
                  className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-yellow hover:text-white flex items-center gap-1"
                  data-testid="checkin-manual-toggle"
                >
                  <MagnifyingGlass size={12} weight="bold" /> Find / manual
                </button>
                {scanning ? (
                  <button onClick={stopScanner} className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-red hover:text-white" data-testid="checkin-stop-btn">
                    Stop camera
                  </button>
                ) : (
                  <button onClick={startScanner} className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-green hover:text-white flex items-center gap-1" data-testid="checkin-start-btn">
                    <Camera size={12} weight="bold" /> Start camera
                  </button>
                )}
              </div>
            </div>

            <div className="relative bg-cyber-black aspect-square w-full max-w-[520px] mx-auto overflow-hidden border border-cyber-line">
              <div id="qr-reader-region" className="absolute inset-0 w-full h-full [&>div]:!border-none [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover" />
              {!scanning && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6" data-testid="scanner-placeholder">
                  <QrCode size={64} weight="duotone" className="text-white/30" />
                  <div className="font-mono-tech text-sm text-white/60 mt-4 uppercase tracking-[0.15em]">Camera off</div>
                  <p className="text-white/40 text-xs mt-2 max-w-xs">
                    Tap "Start camera" to scan attendee QR codes. You may need to allow camera access in your browser.
                  </p>
                </div>
              )}
              {scanning && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[260px] h-[260px] border-2 border-cyber-green shadow-[0_0_40px_rgba(0,229,255,0.35)]" />
                  <div className="absolute top-3 left-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyber-green flex items-center gap-2">
                    <span className="w-2 h-2 bg-cyber-green animate-blink" /> scanning
                  </div>
                </div>
              )}
            </div>

            {cameraError && (
              <div className="mt-3 hard-border border-cyber-red text-cyber-red p-3 font-mono-tech text-xs" data-testid="camera-error">
                / {cameraError}
              </div>
            )}

            {manualOpen && (
              <div className="mt-4 space-y-4" data-testid="manual-area">
                {/* LIVE SEARCH */}
                <div>
                  <div className="term-label mb-2 flex items-center gap-2">
                    <MagnifyingGlass size={12} weight="bold" /> / search by name, email, or company
                  </div>
                  <div className="relative">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                    <input
                      type="text"
                      value={searchQ}
                      onChange={(e) => setSearchQ(e.target.value)}
                      placeholder="Start typing — 'sarah' / 'acme' / 'ada@...'"
                      className="w-full pl-9 pr-3 py-3 bg-cyber-black border border-cyber-line text-white"
                      data-testid="search-input"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                    />
                  </div>
                  <p className="font-mono-tech text-[10px] text-white/40 mt-2">
                    / searches approved RSVPs only{eventId ? ` · limited to ${selectedEvent?.title || "selected event"}` : ""}
                  </p>

                  {searchQ.trim().length >= 2 && (
                    <div className="mt-3 max-h-[340px] overflow-y-auto" data-testid="search-results">
                      {searchBusy && <div className="font-mono-tech text-xs text-white/40 py-2">/ searching...</div>}
                      {!searchBusy && searchResults.length === 0 && (
                        <div className="hard-border bg-cyber-elevated p-3 font-mono-tech text-xs text-white/50">
                          / no approved RSVP matches "{searchQ}". Check spelling, or ask the attendee to confirm their RSVP email.
                        </div>
                      )}
                      {searchResults.map((r) => {
                        const cls = CLASS_BADGE[r.attendee_class || "guest"] || CLASS_BADGE.guest;
                        const Icon = cls.icon;
                        const already = !!r.checked_in_at;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => checkInById(r.id)}
                            disabled={busy}
                            className="w-full text-left hard-border bg-cyber-elevated hover:border-cyber-green transition-colors duration-150 p-3 mb-2 flex items-center justify-between gap-3"
                            data-testid={`search-result-${r.id}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-white font-bold truncate">{r.name}</span>
                                <span className={`inline-flex items-center gap-1 font-mono-tech text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${cls.color}`}>
                                  <Icon size={10} weight="bold" /> {cls.label}
                                </span>
                                {already && (
                                  <span className="inline-flex items-center gap-1 font-mono-tech text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-cyber-yellow text-cyber-yellow">
                                    <Warning size={10} weight="bold" /> Inside
                                  </span>
                                )}
                              </div>
                              <div className="font-mono-tech text-[11px] text-white/60 mt-1 truncate">
                                {r.email}{r.company ? ` · ${r.company}` : ""}
                              </div>
                              <div className="font-mono-tech text-[10px] text-white/40 mt-0.5 truncate">{r.event_title}</div>
                            </div>
                            <CheckCircle size={18} weight="bold" className="text-cyber-green shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* QR PASTE (advanced) */}
                <form onSubmit={handleManual} data-testid="manual-form">
                  <div className="term-label mb-2 flex items-center gap-2">
                    <Keyboard size={12} weight="bold" /> / paste qr payload (advanced)
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={manualValue}
                      onChange={(e) => setManualValue(e.target.value)}
                      placeholder="FTO|event_id|rsvp_id|email — or just the RSVP id"
                      className="flex-1 px-3 py-2 bg-cyber-black border border-cyber-line text-white font-mono-tech text-xs"
                      data-testid="manual-input"
                    />
                    <button type="submit" disabled={busy || !manualValue.trim()} className="btn-ghost-neon" data-testid="manual-submit">Check in</button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* RESULT CARD */}
          {result && (
            <ResultCard result={result} onDismiss={() => setResult(null)} />
          )}
        </div>

        {/* RIGHT: stats + recent */}
        <div className="lg:col-span-5 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <StatBox label="Approved" value={stats.approved} />
            <StatBox label="Inside" value={stats.checked_in} accent="text-cyber-green" />
            <StatBox label="Still out" value={stats.outstanding} accent="text-cyber-yellow" />
          </div>

          <div className="hard-border bg-cyber-surface p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="term-label">/ recently checked in</div>
              <button onClick={loadStats} className="text-white/40 hover:text-white" title="Refresh" data-testid="checkin-refresh">
                <ArrowCounterClockwise size={14} />
              </button>
            </div>
            {recent.length === 0 ? (
              <div className="font-mono-tech text-xs text-white/40 py-4">/ nobody's scanned in yet.</div>
            ) : (
              <div className="space-y-2 max-h-[520px] overflow-y-auto">
                {recent.map((r) => {
                  const cls = CLASS_BADGE[r.attendee_class || "guest"] || CLASS_BADGE.guest;
                  const Icon = cls.icon;
                  return (
                    <div key={r.id} className="border-b border-cyber-line/60 pb-2" data-testid={`recent-${r.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white text-sm truncate">{r.name}</span>
                        <span className={`inline-flex items-center gap-1 font-mono-tech text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${cls.color} shrink-0`}>
                          <Icon size={10} weight="bold" /> {cls.label}
                        </span>
                      </div>
                      <div className="font-mono-tech text-[11px] text-white/50 mt-0.5 truncate">
                        {r.company || "—"} · {r.event_title}
                      </div>
                      <div className="font-mono-tech text-[10px] text-white/40 mt-1">
                        {r.checked_in_at ? new Date(r.checked_in_at).toLocaleTimeString() : ""} · by {r.checked_in_by}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="hard-border bg-cyber-elevated p-4 text-xs text-white/60 leading-relaxed">
            <div className="term-label mb-2">/ tips</div>
            <ul className="space-y-1.5 font-mono-tech">
              <li>· If the camera won't start, tap "Manual" and paste the QR string.</li>
              <li>· "Wrong event" means the QR is from another FintechOmni event.</li>
              <li>· "Already checked in" is a soft warning — you can still admit.</li>
              <li>· <Link to="/admin/login" className="text-cyber-yellow underline">Admin area</Link> is separate from this screen.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value, accent }) => (
  <div className="hard-border bg-cyber-surface p-4">
    <div className="font-mono-tech text-[9px] text-white/50 uppercase tracking-[0.2em]">/ {label}</div>
    <div className={`font-display font-black text-4xl mt-1 tracking-tighter ${accent || "text-white"}`}>{value}</div>
  </div>
);

const ResultCard = ({ result, onDismiss }) => {
  const ok = result.ok;
  const alreadyIn = result.code === "already_checked_in";
  const cls = result.attendee?.attendee_class || "guest";
  const badge = CLASS_BADGE[cls] || CLASS_BADGE.guest;
  const Icon = ok && !alreadyIn ? CheckCircle : ok && alreadyIn ? Warning : XCircle;
  const tint = ok && !alreadyIn ? "border-cyber-green bg-cyber-green/10" : alreadyIn ? "border-cyber-yellow bg-cyber-yellow/10" : "border-cyber-red bg-cyber-red/10";
  const iconTint = ok && !alreadyIn ? "text-cyber-green" : alreadyIn ? "text-cyber-yellow" : "text-cyber-red";

  return (
    <div className={`hard-border p-5 sm:p-7 ${tint}`} data-testid="scan-result">
      <div className="flex items-start gap-4">
        <Icon size={48} weight="duotone" className={`${iconTint} shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className={`font-mono-tech text-[10px] uppercase tracking-[0.25em] ${iconTint}`}>
            / {ok && !alreadyIn ? "admit" : alreadyIn ? "already inside" : "do not admit"}
          </div>
          <div className="font-display font-black tracking-tighter text-3xl sm:text-4xl mt-1 text-white truncate">
            {result.attendee?.name || "Unknown"}
          </div>
          {result.attendee?.company && (
            <div className="text-white/80 mt-1 text-sm">{result.attendee.company}{result.attendee?.position ? ` · ${result.attendee.position}` : ""}</div>
          )}
          {result.attendee?.event_title && (
            <div className="font-mono-tech text-xs text-white/60 mt-1">{result.attendee.event_title}</div>
          )}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {ok && (
              <span className={`inline-flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${badge.color}`}>
                <badge.icon size={11} weight="bold" /> {badge.label}{result.attendee?.sponsor_name ? ` · ${result.attendee.sponsor_name}` : ""}
              </span>
            )}
            {result.attendee?.attendee_type && (
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/60 px-2 py-1 border border-cyber-line">
                {result.attendee.attendee_type}
              </span>
            )}
          </div>
          <p className="text-white/80 text-sm mt-4 leading-relaxed">{result.message}</p>
        </div>
      </div>
      <button onClick={onDismiss} className="btn-ghost-neon w-full justify-center mt-5" data-testid="result-dismiss">
        Next scan
      </button>
    </div>
  );
};

export default CheckinScreen;
